import type { View } from '../router/router';
import { router } from '../router/router';
import { apiClient } from '../services/api-client';
import { toast } from '../components/layout/toast';
import { $ } from '../utils/dom';
import { formatDate } from '../utils/admin-labels';
import { renderConfirmModal, bindModal, showModal } from '../utils/ui';

interface Student {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface PhysicalMeasure {
  id: string;
  weight: number;
  height: number;
  bodyFatPercentage?: number;
  muscleMass?: number;
  chest?: number;
  waist?: number;
  hips?: number;
  armLeft?: number;
  armRight?: number;
  thighLeft?: number;
  thighRight?: number;
  date: string;
  studentId: string;
}

import { getTyrisIcon } from '../components/ui/tyris-icon';

export class AdminMeasuresPage implements View {
  private students: Student[] = [];
  private selectedStudentId = '';
  private history: PhysicalMeasure[] = [];
  private loading = false;
  private editingId: string | null = null;
  private showForm = false;
  private deleteMeasureId: string | null = null;

  async render(): Promise<string> {
    const params = router.getParams();
    if (params.studentId && params.studentId !== this.selectedStudentId) {
      this.selectedStudentId = params.studentId;
      this.history = [];
    }
    this.showForm = params.form === 'new' || params.form === 'edit';
    this.editingId = params.editId ?? null;

    if (this.students.length === 0) {
      await this.loadStudents();
    }

    if (this.selectedStudentId && this.history.length === 0 && !this.loading) {
      await this.loadHistory();
    }

    const selectedStudent = this.students.find((s) => s.id === this.selectedStudentId);

    const breadcrumbHtml = selectedStudent
      ? `<nav class="admin-breadcrumb">
          <a href="#/admin/students">Alumnos</a>
          <span class="admin-breadcrumb-sep">›</span>
          <a href="#/admin/student?id=${selectedStudent.id}">${selectedStudent.firstName} ${selectedStudent.lastName}</a>
          <span class="admin-breadcrumb-sep">›</span>
          <span>Medidas</span>
        </nav>`
      : `<nav class="admin-breadcrumb">
          <a href="#/admin/students">Alumnos</a>
          <span class="admin-breadcrumb-sep">›</span>
          <span>Medidas</span>
        </nav>`;

    return `
      <div class="dashboard-header">
        <div class="dashboard-title">
          ${breadcrumbHtml}
          <h2 data-testid="admin-measures-title" style="display:flex;align-items:center;gap:0.6rem;">${getTyrisIcon('medidas', { size: 28 })} Medidas Corporales</h2>
          <p>Historial antropométrico por alumno. CRUD completo.</p>
        </div>
      </div>

      <section class="card-glass" style="margin-bottom:1.5rem;" data-testid="admin-measures-selector">
        <h3 class="admin-section-title">Seleccionar alumno</h3>
        <div style="display:flex;gap:1rem;align-items:center;flex-wrap:wrap;">
          <select id="student-select" class="admin-select" data-testid="measures-student-select" style="min-width:260px;">
            <option value="">— Selecciona un alumno —</option>
            ${this.students
              .map(
                (s) =>
                  `<option value="${s.id}" ${s.id === this.selectedStudentId ? 'selected' : ''}>${s.firstName} ${s.lastName} (${s.email})</option>`,
              )
              .join('')}
          </select>
          ${
            selectedStudent
              ? `<a href="#/admin/student?id=${selectedStudent.id}" class="btn btn-outline btn-sm">Ver ficha →</a>`
              : ''
          }
        </div>
      </section>

      ${
        !this.selectedStudentId
          ? `<div class="admin-empty" data-testid="admin-measures-no-student"><p>Selecciona un alumno para ver su historial de medidas.</p></div>`
          : this.renderStudentMeasures(selectedStudent)
      }
    `;
  }

  private renderStudentMeasures(student: Student | undefined): string {
    const latest = this.history[0];

    return `
      ${
        student
          ? `
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:1rem;flex-wrap:wrap;gap:0.75rem;">
          <h3 style="margin:0;" data-testid="admin-measures-student-name">${student.firstName} ${student.lastName}</h3>
          <button id="btn-new-measure" class="btn btn-primary btn-sm" data-testid="btn-new-measure" style="display:inline-flex;align-items:center;gap:0.4rem;">${getTyrisIcon('plus', { size: 16 })} Nueva medida</button>
        </div>
      `
          : ''
      }

      ${this.showForm ? this.renderForm() : ''}

      ${
        latest
          ? `
        <section class="card-glass" style="margin-bottom:1.5rem;" data-testid="admin-measures-summary">
          <h3 class="admin-section-title">Resumen actual</h3>
          <div class="admin-kpi-grid">
            <div class="kpi-card"><span class="kpi-value">${latest.weight} kg</span><span class="kpi-label">Peso</span></div>
            <div class="kpi-card"><span class="kpi-value">${latest.height} cm</span><span class="kpi-label">Altura</span></div>
            ${latest.bodyFatPercentage != null ? `<div class="kpi-card"><span class="kpi-value">${latest.bodyFatPercentage}%</span><span class="kpi-label">% Grasa</span></div>` : ''}
            ${latest.muscleMass != null ? `<div class="kpi-card"><span class="kpi-value">${latest.muscleMass} kg</span><span class="kpi-label">Masa muscular</span></div>` : ''}
            ${latest.waist != null ? `<div class="kpi-card"><span class="kpi-value">${latest.waist} cm</span><span class="kpi-label">Cintura</span></div>` : ''}
          </div>
          <p class="admin-cell-muted" style="margin-top:0.5rem;">Última medición: ${formatDate(latest.date)}</p>
        </section>
      `
          : ''
      }

      <section class="card-glass admin-table-wrap" data-testid="admin-measures-history">
        <h3 class="admin-section-title">Historial de medidas</h3>
        ${
          this.history.length === 0
            ? `<div class="admin-empty"><p>Sin medidas registradas para este alumno.</p></div>`
            : `
          <table class="admin-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Peso</th>
                <th>Altura</th>
                <th>% Grasa</th>
                <th>Masa musc.</th>
                <th>Cintura</th>
                <th>Pecho</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              ${this.history
                .slice()
                .reverse()
                .map(
                  (m) => `
                <tr data-measure-id="${m.id}">
                  <td>${formatDate(m.date)}</td>
                  <td>${m.weight} kg</td>
                  <td>${m.height} cm</td>
                  <td>${m.bodyFatPercentage != null ? m.bodyFatPercentage + '%' : '—'}</td>
                  <td>${m.muscleMass != null ? m.muscleMass + ' kg' : '—'}</td>
                  <td>${m.waist != null ? m.waist + ' cm' : '—'}</td>
                  <td>${m.chest != null ? m.chest + ' cm' : '—'}</td>
                  <td>
                    <button class="btn btn-outline btn-sm btn-edit-measure" data-id="${m.id}" data-testid="btn-edit-measure-${m.id}" title="Editar">${getTyrisIcon('editar', { size: 14 })}</button>
                    <button class="btn btn-danger btn-sm btn-delete-measure" data-id="${m.id}" data-testid="btn-delete-measure-${m.id}" title="Eliminar">${getTyrisIcon('basura', { size: 14 })}</button>
                  </td>
                </tr>
              `,
                )
                .join('')}
            </tbody>
          </table>
        `
        }
      </section>
    `;
  }

  private renderForm(): string {
    const editing = this.editingId ? this.history.find((m) => m.id === this.editingId) : null;
    const today = new Date().toISOString().split('T')[0];

    return `
      <section class="card-glass" style="margin-bottom:1.5rem;" data-testid="admin-measures-form">
        <h3 class="admin-section-title">${editing ? 'Editar medida' : 'Nueva medida'}</h3>
        <form id="form-measure" class="admin-form-grid">
          <label>Fecha *
            <input type="date" name="date" class="admin-input" value="${editing?.date?.split('T')[0] ?? today}" required />
          </label>
          <label>Peso (kg) *
            <input type="number" name="weight" class="admin-input" step="0.1" min="1" value="${editing?.weight ?? ''}" required />
          </label>
          <label>Altura (cm) *
            <input type="number" name="height" class="admin-input" step="0.1" min="50" value="${editing?.height ?? ''}" required />
          </label>
          <label>% Grasa corporal
            <input type="number" name="bodyFatPercentage" class="admin-input" step="0.1" min="0" max="100" value="${editing?.bodyFatPercentage ?? ''}" />
          </label>
          <label>Masa muscular (kg)
            <input type="number" name="muscleMass" class="admin-input" step="0.1" min="0" value="${editing?.muscleMass ?? ''}" />
          </label>
          <label>Pecho (cm)
            <input type="number" name="chest" class="admin-input" step="0.1" min="0" value="${editing?.chest ?? ''}" />
          </label>
          <label>Cintura (cm)
            <input type="number" name="waist" class="admin-input" step="0.1" min="0" value="${editing?.waist ?? ''}" />
          </label>
          <label>Cadera (cm)
            <input type="number" name="hips" class="admin-input" step="0.1" min="0" value="${editing?.hips ?? ''}" />
          </label>
          <label>Brazo izq. (cm)
            <input type="number" name="armLeft" class="admin-input" step="0.1" min="0" value="${editing?.armLeft ?? ''}" />
          </label>
          <label>Brazo der. (cm)
            <input type="number" name="armRight" class="admin-input" step="0.1" min="0" value="${editing?.armRight ?? ''}" />
          </label>
          <label>Muslo izq. (cm)
            <input type="number" name="thighLeft" class="admin-input" step="0.1" min="0" value="${editing?.thighLeft ?? ''}" />
          </label>
          <label>Muslo der. (cm)
            <input type="number" name="thighRight" class="admin-input" step="0.1" min="0" value="${editing?.thighRight ?? ''}" />
          </label>
          <div style="grid-column:1/-1;display:flex;gap:0.75rem;margin-top:0.5rem;">
            <button type="submit" class="btn btn-primary" data-testid="btn-save-measure">${editing ? 'Guardar cambios' : 'Registrar medida'}</button>
            <button type="button" id="btn-cancel-form" class="btn btn-outline">Cancelar</button>
          </div>
        </form>
      </section>

      ${renderConfirmModal({
        id: 'confirm-delete-measure',
        title: '¿Eliminar medida corporal?',
        body: 'Esta acción no se puede deshacer y eliminará permanentemente este registro de medidas del historial del alumno.',
        confirmLabel: 'Eliminar',
        danger: true,
      })}
    `;
  }

  afterRender(): void {
    const select = $('#student-select') as HTMLSelectElement | null;
    select?.addEventListener('change', () => {
      if (select.value) {
        window.location.hash = `#/admin/measures?studentId=${select.value}`;
      } else {
        window.location.hash = '#/admin/measures';
      }
    });

    $('#btn-new-measure')?.addEventListener('click', () => {
      window.location.hash = `#/admin/measures?studentId=${this.selectedStudentId}&form=new`;
    });

    $('#btn-cancel-form')?.addEventListener('click', () => {
      window.location.hash = `#/admin/measures?studentId=${this.selectedStudentId}`;
    });

    document.querySelectorAll('.btn-edit-measure').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = (btn as HTMLElement).dataset.id ?? '';
        window.location.hash = `#/admin/measures?studentId=${this.selectedStudentId}&form=edit&editId=${id}`;
      });
    });

    document.querySelectorAll('.btn-delete-measure').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = (btn as HTMLElement).dataset.id;
        if (id) {
          this.deleteMeasureId = id;
          showModal('confirm-delete-measure');
        }
      });
    });

    bindModal({
      modalId: 'confirm-delete-measure',
      onConfirm: async () => {
        if (this.deleteMeasureId) {
          await this.executeDelete(this.deleteMeasureId);
        }
      },
    });

    const form = $('#form-measure') as HTMLFormElement | null;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const fd = new FormData(form);
      const payload: Record<string, unknown> = {
        studentId: this.selectedStudentId,
        date: String(fd.get('date')),
        weight: Number(fd.get('weight')),
        height: Number(fd.get('height')),
      };

      const optionals = [
        'bodyFatPercentage',
        'muscleMass',
        'chest',
        'waist',
        'hips',
        'armLeft',
        'armRight',
        'thighLeft',
        'thighRight',
      ] as const;
      for (const key of optionals) {
        const val = fd.get(key);
        if (val !== '' && val !== null) payload[key] = Number(val);
      }

      try {
        if (this.editingId) {
          await apiClient.patch<PhysicalMeasure>(`/api/measures/${this.editingId}`, payload);
          toast.success('Medida actualizada.');
        } else {
          await apiClient.post<PhysicalMeasure>('/api/measures', payload);
          toast.success('Medida registrada.');
        }
        window.location.hash = `#/admin/measures?studentId=${this.selectedStudentId}`;
      } catch (err: unknown) {
        const body = (err as { body?: { message?: string } })?.body;
        toast.error(body?.message ?? 'Error al guardar medida.');
      }
    });
  }

  private async loadStudents(): Promise<void> {
    try {
      this.students = await apiClient.get<Student[]>('/api/users/students');
    } catch {
      toast.error('No se pudo cargar el listado de alumnos.');
    }
  }

  private async loadHistory(): Promise<void> {
    this.loading = true;
    try {
      this.history = await apiClient.get<PhysicalMeasure[]>(
        `/api/measures/student/${this.selectedStudentId}`,
      );
    } catch {
      this.history = [];
    } finally {
      this.loading = false;
    }
  }

  private async executeDelete(id: string): Promise<void> {
    try {
      await apiClient.delete(`/api/measures/${id}`);
      toast.success('Medida eliminada.');
      window.location.hash = `#/admin/measures?studentId=${this.selectedStudentId}`;
    } catch {
      toast.error('No se pudo eliminar la medida.');
    } finally {
      this.deleteMeasureId = null;
    }
  }
}
